import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react"
import { useHistory, useParams } from "react-router-dom"
import ListItem from "@material-ui/core/ListItem"
import ListItemAvatar from "@material-ui/core/ListItemAvatar"
import Typography from "@material-ui/core/Typography"
import { MenuItem, Menu } from "@material-ui/core"
import { IconButton, Chip } from "@mui/material"
import { Ellipsis, Check, Undo, Eye, ListChecks, ArrowLeftRightIcon, Lock } from "lucide-react"
import { v4 as uuidv4 } from "uuid"
import api from "../../services/api"
import { AuthContext } from "../../context/Auth/AuthContext"
import { TicketsContext } from "../../context/Tickets/TicketsContext"
import toastError from "../../errors/toastError"
import TicketMessagesDialog from "../TicketMessagesDialog"
import AvatarComBadge from "../AvatarComBadge"
import useStyles from "./styles"
import textoTruncar from "../../utils/textoTruncar"
import formatarTempoRelativo from "../../utils/formatarTempoRelativo"
import MarkdownWrapper from "../MarkdownWrapper"
import { useLocation } from "react-router-dom/cjs/react-router-dom"
import { pegarCorDeFundo } from "../../helpers/pegarCorDeFundo"
import { pegarCorPrimaria } from "../../helpers/pegarCorPrimaria"
import useContactsStore from "../../stores/ContactsStore"
import useConversaRascunhoStore, { selectDraftContentByTicket } from "../../stores/conversaRascunho"
import TransferTicketModalCustom from "../TransferTicketModalCustom"
import { check as canCheck } from "../Can"
import { conversaPodeAssumir } from "../../helpers/conversaSemResponsavel"
import obterNomeContatoConversa from "../../helpers/obterNomeContatoConversa"
import { canais } from "../../utils/canais"
import { prefetchTicketPainel } from "../../utils/prefetchTicketPainel"

const TicketListItemCustom = ({ ticket, showOptionsMenu = true }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [isIconButtonClicked, setIsIconButtonClicked] = useState(false)
  const classes = useStyles()
  const [realativeTime, setRealativeTime] = useState(formatarTempoRelativo(ticket?.ultimaMensagemData))
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false)
  const [lTransferModalAberto, setTransferModalAberto] = useState(false)
  const { ticketId: conversaAtivaUuid } = useParams() // pega o uuid do ticket da URL
  const isMounted = useRef(true)
  const { setCurrentTicket } = useContext(TicketsContext)
  const { user } = useContext(AuthContext)
  const location = useLocation()

  // Selector estável por contactId — re-renderiza apenas quando ESTE contato mudar (padrão CustomCard)
  const contactId = ticket.contact?.id
  const selectorContato = useCallback(
    (state) => state.contacts.find((c) => c.id === contactId) ?? null,
    [contactId],
  )
  const oContatoFromStore = useContactsStore(selectorContato)

  const oTicketParaExibicao = useMemo(() => {
    if (oContatoFromStore) {
      return { ...ticket, contact: oContatoFromStore }
    }
    return ticket
  }, [ticket, oContatoFromStore])

  // Ref para controlar se já está marcando como lida e evitar loop infinito
  const lMarcandoComoLidaRef = useRef(false)

  const [nMensagensNaoLidas, setNMessagensNaoLidas] = useState(() => {
    if (ticket.canalTipoId === canais.interno) {
      return Array.isArray(ticket?.conversaParticipantes)
        ? ticket.conversaParticipantes.find((participante) => participante.usuarioId === user.id)?.mensagensNaoLidas ||
            0
        : 0
    } else {
      return ticket.unreadMessages || 0
    }
  })

  const oRascunho = useConversaRascunhoStore(selectDraftContentByTicket(ticket.id))

  // Constante para determinar se é conversa interna
  const lEhConversaInterna = ticket.canalTipoId === canais.interno

  // Obtém o nome e foto considerando se é conversa interna ou não (contato vivo do store)
  const { cNome: contactName, cFoto: profilePicUrl, cEmpresa: empresa } = obterNomeContatoConversa(
    oTicketParaExibicao,
    user.id,
  )

  // Para conversas internas, determina se deve mostrar dois avatares
  const lUsuarioLogadoParticipa =
    lEhConversaInterna && (ticket.responsavel?.id === user.id || ticket.usuario?.id === user.id)
  const lMostrarDoisAvatares = lEhConversaInterna && !lUsuarioLogadoParticipa
  const cImagemPerfilPrincipal = lMostrarDoisAvatares
    ? ticket.responsavel?.imagemPerfil
    : profilePicUrl || ticket?.contact?.profilePicUrl

  const cNomeAvatarPrincipal = lMostrarDoisAvatares ? ticket.responsavel?.name : contactName

  const cNomeAvatarSecundario = lMostrarDoisAvatares ? ticket.usuario?.name : undefined

  const cAbreviacaoPrincipal = lEhConversaInterna
    ? lMostrarDoisAvatares
      ? ticket.responsavel?.nomeAbreviacao
      : ticket.responsavel?.id === user.id
        ? ticket.usuario?.nomeAbreviacao
        : ticket.usuario?.id === user.id
          ? ticket.responsavel?.nomeAbreviacao
          : ticket.responsavel?.nomeAbreviacao || ticket.usuario?.nomeAbreviacao
    : undefined

  const cAbreviacaoSecundaria = lMostrarDoisAvatares ? ticket.usuario?.nomeAbreviacao : undefined

  const cImagemPerfilSecundaria = lMostrarDoisAvatares ? ticket.usuario?.imagemPerfil : null

  const userAbreviacao = ticket.responsavel?.nomeAbreviacao || ticket.responsavel?.name || "Sem responsável"
  const situacoes = ticket.situacoes || []

  // Determina o tipo de canal baseado no canalTipoId
  const cObterTipoCanal = (nCanalTipoId) => {
    switch (nCanalTipoId) {
      case canais.interno:
        return "interno"
      case canais.whatsapp:
        return "whatsapp"
      default:
        return "whatsapp" // fallback padrão
    }
  }

  const cTipoCanal = cObterTipoCanal(ticket.canalTipoId)

  useEffect(() => {
    // Atualização imediata quando a dependência muda
    setRealativeTime(formatarTempoRelativo(ticket?.ultimaMensagemData))

    // Intervalo para atualizações periódicas
    const interval = setInterval(() => {
      setRealativeTime(formatarTempoRelativo(ticket?.ultimaMensagemData))
    }, 60000) // 60 segundos

    // Recalcula o número de mensagens não lidas a partir do objeto `ticket`
    const unreadFromTicket =
      ticket.canalTipoId === canais.interno
        ? ticket.conversaParticipantes?.find((p) => p.usuarioId === user.id)?.mensagensNaoLidas || 0
        : ticket.unreadMessages || 0

    // Verifica se está na conversa atualmente
    const lEstaAberto = location.pathname.includes(ticket.uuid)
    const lTemMensagensNaoLidas = unreadFromTicket > 0
    const lPrecisaMarcarComoLida = lEstaAberto && lTemMensagensNaoLidas && !lMarcandoComoLidaRef.current

    if (lPrecisaMarcarComoLida) {
      // Chama a API para marcar como lida - handleUpdateticket já gerencia a ref
      handleUpdateticket(ticket.id)
      setNMessagensNaoLidas(0) // Atualiza imediatamente a interface para refletir que as mensagens foram marcadas como lidas
    } else if (!lEstaAberto && nMensagensNaoLidas !== unreadFromTicket) {
      // Atualiza estado local quando não está na conversa
      setNMessagensNaoLidas(unreadFromTicket)
    }

    // Limpeza
    return () => clearInterval(interval)
  }, [
    ticket?.ultimaMensagemData,
    ticket?.uuid,
    ticket.ultimaMensagem,
    ticket?.conversaParticipantes,
    ticket?.unreadMessages,
    user?.id,
    location.pathname,
  ])

  const handleCloseTicket = async (id) => {
    setLoading(true)
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        queueId: ticket?.queue?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null,
        canalTipoId: ticket.canalTipoId,
      })

      setTimeout(() => {
        history.push("/conversas/")
      }, 100)
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
    if (isMounted.current) {
      setLoading(false)
    }
  }

  const handleReopenTicket = async (id) => {
    setLoading(true)
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        responsavelId: user?.id,
        queueId: ticket?.queue?.id,
        canalTipoId: ticket.canalTipoId,
      })
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
    if (isMounted.current) {
      setLoading(false)
    }
    history.push(`/conversas/${ticket.uuid}`)
  }

  const handleUpdateticket = async (id) => {
    // Evita chamadas simultâneas que causam loop infinito
    if (lMarcandoComoLidaRef.current) {
      return
    }

    try {
      lMarcandoComoLidaRef.current = true
      await api.put(`/tickets/${id}`)
    } catch (e) {
      toastError(e)
    } finally {
      // Resetar após delay para evitar múltiplas chamadas consecutivas
      setTimeout(() => {
        lMarcandoComoLidaRef.current = false
      }, 1000)
    }
  }
  const handleAcepptTicket = async (id) => {
    setLoading(true)
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        responsavelId: user?.id,
        canalTipoId: ticket.canalTipoId,
      })

      /** Verifica se está configurado para enviar mensagem de saudação ao aceitar a conversa. */
      let mConfiguracao

      try {
        const { data } = await api.get("/settings/")
        mConfiguracao = data.filter((s) => s.key === "sendGreetingAccepted")

        if (mConfiguracao[0] && mConfiguracao[0].value === "enabled" && !ticket.isGroup) {
          handleSendMessage(ticket.id)
        }
      } catch (oErro) {
        toastError(oErro)
      }
      if (isMounted.current) {
        setLoading(false)
        history.push(`/conversas/${ticket.uuid}`)
      }
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
  }

  const handleSendMessage = async (id) => {
    const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com a conversa!`
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `*Mensagem Automática:*\n${msg.trim()}`,
      canalTipoId: ticket.canalTipoId,
    }
    try {
      await api.post(`/messages/${id}`, message)
    } catch (err) {
      toastError(err)
    }
  }

  const lRascunhoApresentar = !location.pathname.includes(ticket.uuid) && oRascunho && showOptionsMenu

  const handleSelectTicket = (ticket) => {
    const code = uuidv4()
    const { id, uuid } = ticket
    setCurrentTicket({ ...ticket, id, uuid, code })
  }

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setMenuOpen(false)
  }

  if (!ticket) return null

  return (
    <div style={{ width: "100%" }}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => {
          setOpenTicketMessageDialog(false)
          handleCloseMenu()
        }}
        ticketId={ticket.id}
        ticketInicial={ticket}
      ></TicketMessagesDialog>

      {/* Exibe modal de transferência apenas se NÃO for conversa interna */}
      {!lEhConversaInterna && (
        <TransferTicketModalCustom
          modalOpen={lTransferModalAberto}
          onClose={() => {
            // Apenas fecha o modal; o menu já é fechado antes de abrir o modal
            setTransferModalAberto(false)
          }}
          ticketid={ticket.id}
        />
      )}

      <ListItem
        data-testid={`ticket-list-item-id-${ticket.id}`}
        dense
        button
        disableRipple={isIconButtonClicked}
        onMouseEnter={prefetchTicketPainel}
        onFocus={prefetchTicketPainel}
        onClick={() => {
          // Ao clicar em ticket sem responsável (pendente ou sem user), abrir "Espiar" direto
          if (ticket.status === "pending" || !ticket.responsavelId) {
            setOpenTicketMessageDialog(true)
            setIsIconButtonClicked(false)
            return
          }
          // Caso contrário, seleciona normalmente a conversa
          handleSelectTicket(ticket)
          setIsIconButtonClicked(false)
        }}
        selected={conversaAtivaUuid && ticket.uuid === conversaAtivaUuid}
        className={classes.ticket}
      >
        <ListItemAvatar className={classes.avatar}>
          <AvatarComBadge
            cSrcPrincipal={cImagemPerfilPrincipal || ticket?.contact?.profilePicUrl}
            cSrcSecundario={cImagemPerfilSecundaria}
            lMostrarDoisAvatares={lMostrarDoisAvatares}
            cNomePrincipal={cNomeAvatarPrincipal}
            cNomeAbreviacao={cAbreviacaoPrincipal}
            cNomeSecundario={cNomeAvatarSecundario}
            cNomeAbreviacaoSecundario={cAbreviacaoSecundaria}
            cTipoCanal={lMostrarDoisAvatares ? null : cTipoCanal}
            nTamanho={48}
          />
        </ListItemAvatar>

        <div className={classes.content}>
          <div className={classes.header}>
            <Typography variant="body1" className={classes.name} data-testid="ticket-card-contact-name">
              {/* Para conversas internas, não exibir empresa */}
              {!lEhConversaInterna && empresa
                ? textoTruncar(contactName.trim().split(" ")[0], 13) + " - " + textoTruncar(empresa, 30)
                : textoTruncar(contactName, 30)}
            </Typography>
          </div>
          <div className={classes.messageRow}>
            {nMensagensNaoLidas > 0 && (
              <div
                data-testid="ticket-unread-badge"
                style={{
                  backgroundColor: "#009FFD",
                  color: "white",
                  fontWeight: 400,
                  fontSize: "12px",
                  letterSpacing: "-0.25%",
                  height: "15px",
                  width: "15px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  lineHeight: "15px",
                  paddingTop: "2px",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  gap: "10px",
                  marginRight: "4px",
                }}
              >
                {nMensagensNaoLidas}
              </div>
            )}
            <Typography
              variant="body2"
              noWrap
              component="div"
              className={classes.description}
              data-testid="ticket-card-last-message"
            >
              <MarkdownWrapper>
                {lRascunhoApresentar
                  ? "*Rascunho: *" + textoTruncar(oRascunho, 30)
                  : textoTruncar(ticket.ultimaMensagem || "", 30)}
              </MarkdownWrapper>
            </Typography>
          </div>

          <div className={classes.situacoes}>
            <Chip
              label={realativeTime}
              style={{
                backgroundColor: "#E6F5ED",
                color: "#029C49",
                fontWeight: 400,
                height: "19px",
              }}
              size="small"
            />
            {/* Para conversas internas, não exibir situações, apenas o chip 'Conversa Interna' */}
            {!lEhConversaInterna && situacoes.length > 0 ? (
              situacoes.map((t) => (
                <Chip
                  key={t.id}
                  label={t.name}
                  style={{
                    backgroundColor: "#F4F4F4",
                    color: "#969696",
                    fontWeight: 400,
                    height: "19px",
                  }}
                  size="small"
                />
              ))
            ) : (
              <Chip
                icon={lEhConversaInterna ? <Lock size={12} /> : undefined}
                label={lEhConversaInterna ? "Chat interno" : ticket.motivoContato?.descricao || "Sem motivo de contato"}
                style={{
                  backgroundColor: lEhConversaInterna ? "#F4F4F4" : pegarCorDeFundo(ticket.motivoContato?.cor),
                  color: lEhConversaInterna
                    ? "#969696"
                    : pegarCorPrimaria(ticket.motivoContato?.cor) || pegarCorPrimaria("#969696"),
                  fontWeight: 400,
                  height: "19px",
                }}
                classes={lEhConversaInterna ? { icon: classes.chipIcon } : undefined}
                size="small"
              />
            )}
          </div>
          {/* Para conversas internas, não exibir linha do responsável */}
          {!lEhConversaInterna && (
            <Typography variant="body2" className={classes.responsavel}>
              {userAbreviacao} - {ticket.situacao?.name || "Sem situação"}
            </Typography>
          )}
        </div>

        {showOptionsMenu && (
          <IconButton
            aria-label="Abrir opções da conversa"
            onClick={(e) => {
              handleMenu(e)
              e.stopPropagation()
              setIsIconButtonClicked(true)
            }}
            className={classes.iconeBotao}
            disableRipple
          >
            <Ellipsis size={"24px"} strokeWidth={1.6} className={classes.iconOptions} />
          </IconButton>
        )}
        <Menu
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={(e) => {
            handleCloseMenu(e)
            e.stopPropagation()
          }}
          getContentAnchorEl={null}
          classes={{ paper: classes.menuActions }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          style={{
            marginTop: 35,
            borderRadius: "12px",
            boxShadow: "0 4px 4px rgba(0, 0, 0, 0.2)",
          }}
        >
          <div className={classes.menu}>
            <MenuItem
              className={classes.menuItem}
              onClick={(e) => {
                e.stopPropagation()
                setOpenTicketMessageDialog(true)
              }}
            >
              <Eye strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
              <div className={classes.textMenuPerfil}>Espiar</div>
            </MenuItem>

            {/* Exibe opção de transferir apenas se NÃO for conversa interna */}
            {!lEhConversaInterna && canCheck(user?.profile, "tickets:transferTicket") && (
              <MenuItem
                className={classes.menuItem}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCloseMenu()
                  setTransferModalAberto(true)
                }}
              >
                <ArrowLeftRightIcon strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
                <div className={classes.textMenuPerfil}>Transferir</div>
              </MenuItem>
            )}

            {conversaPodeAssumir(ticket, { lEhConversaInterna }) && (
              <MenuItem className={classes.menuItem} onClick={() => handleAcepptTicket(ticket.id)}>
                <ListChecks strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
                <div className={classes.textMenuPerfil}>Assumir</div>
              </MenuItem>
            )}

            {ticket.status === "closed" && (
              <MenuItem className={classes.menuItem} disabled={loading} onClick={() => handleReopenTicket(ticket.id)}>
                <Undo strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
                <div className={classes.textMenuPerfil}>Reabrir</div>
              </MenuItem>
            )}

            {ticket.status !== "closed" && (
              <MenuItem className={classes.menuItem} disabled={loading} onClick={() => handleCloseTicket(ticket.id)}>
                <Check className={classes.iconMenu} />
                <div className={classes.textMenuPerfil}>Finalizar</div>
              </MenuItem>
            )}
          </div>
        </Menu>
      </ListItem>
    </div>
  )
}

const lContatoExibicaoIgual = (oContatoAnterior, oContatoProximo) => {
  if (oContatoAnterior === oContatoProximo) return true
  if (!oContatoAnterior || !oContatoProximo) return false
  return (
    oContatoAnterior.name === oContatoProximo.name &&
    oContatoAnterior.nome === oContatoProximo.nome &&
    oContatoAnterior.profilePicUrl === oContatoProximo.profilePicUrl &&
    oContatoAnterior.imagemPerfil === oContatoProximo.imagemPerfil &&
    oContatoAnterior.empresa === oContatoProximo.empresa &&
    oContatoAnterior.number === oContatoProximo.number
  )
}

const lPropsSaoIguais = (oPropsAnterior, oProximoProps) => {
  const oTicketAnterior = oPropsAnterior.ticket
  const oProximoTicket = oProximoProps.ticket
  return (
    oTicketAnterior.id === oProximoTicket.id &&
    oTicketAnterior.status === oProximoTicket.status &&
    oTicketAnterior.ultimaMensagem === oProximoTicket.ultimaMensagem &&
    oTicketAnterior.ultimaMensagemData === oProximoTicket.ultimaMensagemData &&
    oTicketAnterior.unreadMessages === oProximoTicket.unreadMessages &&
    oTicketAnterior.responsavelId === oProximoTicket.responsavelId &&
    oTicketAnterior.situacaoId === oProximoTicket.situacaoId &&
    oTicketAnterior.conversaParticipantes === oProximoTicket.conversaParticipantes &&
    lContatoExibicaoIgual(oTicketAnterior.contact, oProximoTicket.contact) &&
    oPropsAnterior.showOptionsMenu === oProximoProps.showOptionsMenu
  )
}

export default React.memo(TicketListItemCustom, lPropsSaoIguais)
